import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { IntakeSectionsView } from "@/components/admin/IntakeSectionsView";

const STATUSES = ["nuevo","pagado","intake_pendiente","en_proceso","esperando_cliente","completado","cancelado"] as const;
const STATUS_LABELS: Record<string, string> = {
  nuevo: "Pago pendiente",
  pagado: "Pagado",
  intake_pendiente: "Formulario pendiente",
  en_proceso: "En proceso",
  esperando_cliente: "Esperando cliente",
  completado: "Completado",
  cancelado: "Cancelado",
};

const EVENT_LABELS: Record<string, string> = {
  order_created: "Pedido creado",
  payment_confirmed: "Pago confirmado",
  intake_submitted: "Formulario enviado",
  status_changed: "Estado cambiado",
  note_added: "Nota interna agregada",
  customer_contacted: "Cliente contactado",
  form_reviewed: "Formulario revisado",
  order_archived: "Pedido archivado",
  order_completed: "Pedido completado",
};

export const Route = createFileRoute("/admin/pedidos/$id")({ component: PedidoDetalle });

async function downloadLegalPdf(orderId: string, kind: "policies" | "authorization") {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { toast.error("Sesión expirada"); return; }
    const res = await fetch(`/api/admin/legal-pdf/${orderId}/${kind}`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      toast.error(`No se pudo descargar (${res.status}) ${txt}`);
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${kind}-${orderId.slice(0, 8)}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e: any) {
    toast.error(e?.message ?? "Error descargando PDF");
  }
}

function PedidoDetalle() {
  const { id } = Route.useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [intake, setIntake] = useState<any>(null);
  const [notes, setNotes] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [legals, setLegals] = useState<any[]>([]);
  const [newNote, setNewNote] = useState("");
  const [showRawJson, setShowRawJson] = useState(false);

  async function load() {
    const { data: o } = await supabase
      .from("orders")
      .select("*,customers(*)")
      .eq("id", id).maybeSingle();
    if (!o) { toast.error("Pedido no encontrado"); return; }
    setOrder(o);

    const [{ data: it }, { data: f }, { data: n }, { data: ev }, { data: la }] = await Promise.all([
      supabase.from("order_items").select("*").eq("order_id", id).order("created_at"),
      supabase.from("intake_forms").select("*").eq("order_id", id).order("submitted_at", { ascending: false }).limit(1).maybeSingle(),
      supabase.from("order_notes").select("*").eq("order_id", id).order("created_at", { ascending: false }),
      supabase.from("order_events").select("*").eq("order_id", id).order("created_at", { ascending: false }),
      supabase.from("legal_acceptances").select("*").eq("order_id", id).order("accepted_at", { ascending: true }),
    ]);
    setItems(it ?? []);
    setIntake(f ?? null);
    setNotes(n ?? []);
    setEvents(ev ?? []);
    setLegals(la ?? []);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Si el URL trae #formulario (e.g. desde /admin/intakes), hacer scroll a la sección
  useEffect(() => {
    if (!order) return;
    if (typeof window === "undefined") return;
    const hash = window.location.hash.replace("#", "");
    if (!hash) return;
    // esperar a que el DOM tenga la sección renderizada
    const t = setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => clearTimeout(t);
  }, [order]);

  async function setStatus(s: string) {
    const { error } = await supabase.from("orders").update({ status: s as any }).eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Estado actualizado"); load();
  }

  async function toggle(field: string, value: boolean) {
    const { error } = await supabase.from("orders").update({ [field]: value } as any).eq("id", id);
    if (error) return toast.error(error.message);
    // Log specific events
    if (field === "customer_contacted" && value) {
      await supabase.from("order_events").insert({ order_id: id, event_type: "customer_contacted", actor: "admin" });
    } else if (field === "form_reviewed" && value) {
      await supabase.from("order_events").insert({ order_id: id, event_type: "form_reviewed", actor: "admin" });
    } else if (field === "archived" && value) {
      await supabase.from("order_events").insert({ order_id: id, event_type: "order_archived", actor: "admin" });
    }
    load();
  }

  async function addNote() {
    if (!newNote.trim()) return;
    const { data: u } = await supabase.auth.getUser();
    const { error } = await supabase.from("order_notes").insert({
      order_id: id, note: newNote.trim(), author_id: u.user?.id ?? null,
    });
    if (error) return toast.error(error.message);
    await supabase.from("order_events").insert({
      order_id: id, event_type: "note_added", actor: "admin",
      payload: { preview: newNote.trim().slice(0, 80) },
    });
    setNewNote(""); toast.success("Nota guardada"); load();
  }

  async function deleteNote(noteId: string) {
    if (!confirm("¿Eliminar esta nota interna?")) return;
    const { error } = await supabase.from("order_notes").delete().eq("id", noteId);
    if (error) return toast.error(error.message);
    load();
  }

  if (!order) return <div className="text-muted-foreground">Cargando…</div>;

  const c = order.customers;
  const isUrgent = order.urgent_manual === true || order.operational_answers?.urgent === "true";
  const stripeSearchUrl = order.stripe_session_id
    ? `https://dashboard.stripe.com/payments?query=${encodeURIComponent(order.stripe_session_id)}`
    : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/admin/pedidos" className="text-xs text-muted-foreground hover:text-foreground">← Volver a pedidos</Link>
          <h2 className="mt-1 font-display text-xl font-semibold text-foreground">
            Pedido <span className="font-mono text-base text-muted-foreground">{order.id.slice(0, 8)}</span>
            {isUrgent && <span className="ml-2 rounded bg-primary/20 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-primary align-middle">Urgente</span>}
            {order.archived && <span className="ml-2 rounded bg-muted px-2 py-0.5 text-[11px] uppercase tracking-wider text-muted-foreground align-middle">Archivado</span>}
          </h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <select value={order.status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-hairline bg-surface px-3 py-1.5 text-sm">
            {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </select>
          {stripeSearchUrl && (
            <a href={stripeSearchUrl} target="_blank" rel="noreferrer" className="rounded-md border border-hairline bg-surface px-3 py-1.5 text-sm text-foreground hover:bg-surface-2">Ver en Stripe ↗</a>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* LEFT column (2/3) */}
        <div className="space-y-6 lg:col-span-2">
          {/* Resumen + Cliente + Items */}
          <Section title="Resumen del pedido">
            <Grid>
              <Field label="ID completo"><span className="font-mono text-xs">{order.id}</span></Field>
              <Field label="Estado"><span className="font-medium">{STATUS_LABELS[order.status]}</span></Field>
              <Field label="Creado">{new Date(order.created_at).toLocaleString()}</Field>
              <Field label="Última actualización">{new Date(order.updated_at).toLocaleString()}</Field>
              <Field label="Pagado el">{order.paid_at ? new Date(order.paid_at).toLocaleString() : "—"}</Field>
              <Field label="Stripe session ID">
                {order.stripe_session_id ? (
                  <button onClick={() => { navigator.clipboard.writeText(order.stripe_session_id); toast.success("Copiado"); }} className="font-mono text-xs hover:text-foreground">{order.stripe_session_id}</button>
                ) : <span className="text-muted-foreground">—</span>}
              </Field>
              <Field label="Subtotal">{money(order.subtotal)}</Field>
              <Field label="Total">{money(order.total)}</Field>
            </Grid>
          </Section>

          <Section title="Cliente">
            {c ? (
              <>
                <Grid>
                  <Field label="Nombre">{c.full_name}</Field>
                  <Field label="Email"><a href={`mailto:${c.email}`} className="hover:underline">{c.email}</a></Field>
                  <Field label="Teléfono">{c.phone ? <a href={`tel:${c.phone}`} className="hover:underline">{c.phone}</a> : "—"}</Field>
                  <Field label="Contacto preferido">{c.preferred_contact ?? "—"}</Field>
                  <Field label="Compañía">{c.company_name ?? "—"}</Field>
                  <Field label="Estado">{c.state ?? "—"}</Field>
                  <Field label="Cuenta">{c.user_id ? "👤 Sí" : "No"}</Field>
                </Grid>
                <div className="mt-3">
                  <Link to="/admin/clientes/$id" params={{ id: c.id }} className="text-xs text-primary hover:underline">Ver perfil del cliente →</Link>
                </div>
              </>
            ) : <p className="text-sm text-muted-foreground">Sin cliente asociado.</p>}
          </Section>

          <Section title="Items comprados">
            <ul className="divide-y divide-hairline">
              {items.map((it) => (
                <li key={it.id} className="flex items-center justify-between py-2 text-sm">
                  <div>
                    <div className="text-foreground">{it.name_snapshot}</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{it.item_type}{it.qty > 1 ? ` · x${it.qty}` : ""}</div>
                  </div>
                  <div className="font-medium text-foreground">{money(it.price_snapshot)}</div>
                </li>
              ))}
            </ul>
            <div className="mt-3 flex justify-between border-t border-hairline pt-3 text-sm">
              <span className="text-muted-foreground">Total</span>
              <span className="font-display text-base font-semibold text-foreground">{money(order.total)}</span>
            </div>
          </Section>

          <Section title="Formulario de datos del pedido" anchorId="formulario">
            {!intake ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="rounded bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400">Formulario pendiente</span>
                  <span className="text-xs text-muted-foreground">El cliente aún no ha enviado los datos.</span>
                </div>
                {!order.paid_at ? (
                  <div className="rounded-md border border-yellow-500/40 bg-yellow-500/10 p-3 text-xs text-foreground">
                    Disponible después del pago confirmado. El link del formulario solo se habilita cuando la orden está pagada.
                  </div>
                ) : order.intake_token ? (
                  <>
                    <div className="rounded-md border border-hairline bg-surface-2 p-2 font-mono text-[11px] text-muted-foreground break-all">
                      {typeof window !== "undefined" ? `${window.location.origin}/intake/${order.intake_token}` : `/intake/${order.intake_token}`}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        onClick={() => {
                          const url = `${window.location.origin}/intake/${order.intake_token}`;
                          navigator.clipboard.writeText(url);
                          toast.success("Link copiado");
                        }}
                        className="bg-primary text-primary-foreground hover:bg-primary/90"
                      >
                        Copiar link del formulario
                      </Button>
                      <a
                        href={`/intake/${order.intake_token}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center rounded-md border border-hairline bg-surface px-3 py-1.5 text-xs text-foreground hover:bg-surface-2"
                      >
                        Abrir link ↗
                      </a>
                    </div>
                  </>
                ) : (
                  <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
                    ⚠ Este pedido no tiene link de formulario disponible (sin <code>intake_token</code>). Contacta a soporte técnico antes de enviar nada al cliente.
                  </div>
                )}
              </div>
            ) : (
              <div>
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <span className="rounded bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">Formulario enviado</span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(intake.submitted_at).toLocaleString()} · Por {intake.legal_full_name ?? intake.email ?? "—"}
                  </span>
                  <Link
                    to="/admin/formularios/$id"
                    params={{ id: intake.id }}
                    className="ml-auto inline-flex items-center rounded-md border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/20"
                  >
                    Ver formulario completo →
                  </Link>
                </div>
                {intake.sections ? <IntakeSectionsView sections={intake.sections} /> : (
                  <pre className="max-h-96 overflow-auto rounded bg-surface-2 p-3 text-xs text-muted-foreground">{JSON.stringify(intake.payload, null, 2)}</pre>
                )}

                {intake.sections && (
                  <details className="mt-4" open={showRawJson} onToggle={(e) => setShowRawJson((e.target as HTMLDetailsElement).open)}>
                    <summary className="cursor-pointer text-[11px] uppercase tracking-wider text-muted-foreground">Ver JSON crudo (técnico)</summary>
                    <pre className="mt-2 max-h-96 overflow-auto rounded bg-surface-2 p-3 text-xs text-muted-foreground">{JSON.stringify(intake.payload, null, 2)}</pre>
                  </details>
                )}
              </div>
            )}
          </Section>

          <Section title="Aceptaciones legales firmadas" anchorId="legales">
            {(() => {
              const finalAcceptance = legals.find(
                (l) => l.accepted_documents_snapshot && l.snapshot_hash && l.typed_signature && l.legal_full_name,
              );
              const customerEmail = order.customers?.email ?? "—";
              return (
                <div className="space-y-4">
                  {finalAcceptance ? (
                    <>
                      <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 text-xs text-emerald-700 dark:text-emerald-300">
                        Firma final registrada
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={() => downloadLegalPdf(id, "policies")}>
                          Descargar PDF — Políticas legales
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => downloadLegalPdf(id, "authorization")}>
                          Descargar PDF — Autorización, Service Agreement y firma electrónica
                        </Button>
                      </div>
                      <div className="rounded-md border border-hairline bg-surface-2 p-3 text-sm">
                        <Grid>
                          <Field label="Nombre legal firmado">{finalAcceptance.legal_full_name || "—"}</Field>
                          <Field label="Compañía">{finalAcceptance.company_name || "—"}</Field>
                          <Field label="Signer title">{finalAcceptance.signer_title || "—"}</Field>
                          <Field label="Email del cliente">{customerEmail}</Field>
                          <Field label="Fecha y hora de firma">{new Date(finalAcceptance.accepted_at).toLocaleString()}</Field>
                          <Field label="Snapshot hash">
                            <span className="font-mono text-[10px]" title={finalAcceptance.snapshot_hash}>
                              {String(finalAcceptance.snapshot_hash).slice(0, 16)}…
                            </span>
                          </Field>
                          <Field label="IP">{finalAcceptance.ip_address || "—"}</Field>
                          <Field label="User agent">
                            <span className="block max-w-full truncate font-mono text-[10px]" title={finalAcceptance.user_agent}>
                              {finalAcceptance.user_agent || "—"}
                            </span>
                          </Field>
                        </Grid>
                        <div className="mt-3 grid grid-cols-1 gap-1 text-[11px] sm:grid-cols-2">
                          <LegalCheck label="Service Agreement" ok={finalAcceptance.accepted_service_agreement} version={finalAcceptance.service_agreement_version} />
                          <LegalCheck label="Limited Authorization" ok={finalAcceptance.accepted_limited_authorization} version={finalAcceptance.limited_authorization_version} />
                          <LegalCheck label="Consentimiento firma electrónica" ok={finalAcceptance.accepted_electronic_signature_consent} version={finalAcceptance.electronic_signature_consent_version} />
                          <LegalCheck label="Certificación de información" ok={finalAcceptance.accepted_information_certification} />
                          <LegalCheck label="Privacidad / datos" ok={finalAcceptance.accepted_privacy_data_consent} version={finalAcceptance.privacy_policy_version} />
                          <LegalCheck label="Política de reembolso" ok={finalAcceptance.accepted_refund_policy} version={finalAcceptance.refund_policy_version} />
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-300">
                        Pendiente de firma final — Disponible después de que el cliente complete y firme el intake.
                      </div>
                      <div className="flex flex-wrap gap-2 opacity-60">
                        <Button size="sm" variant="outline" disabled>Descargar PDF — Políticas legales</Button>
                        <Button size="sm" variant="outline" disabled>Descargar PDF — Autorización, Service Agreement y firma electrónica</Button>
                      </div>
                      {legals.length > 0 && (
                        <details className="text-xs text-muted-foreground">
                          <summary className="cursor-pointer">Ver registros preliminares ({legals.length})</summary>
                          <ul className="mt-2 space-y-2">
                            {legals.map((l) => (
                              <li key={l.id} className="rounded border border-hairline bg-surface-2 p-2">
                                <div className="flex justify-between">
                                  <span className="font-mono text-[10px]">{l.source_route ?? "—"}</span>
                                  <span className="text-[10px]">{new Date(l.accepted_at).toLocaleString()}</span>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </>
                  )}
                </div>
              );
            })()}
          </Section>
        </div>

        {/* RIGHT column (1/3) */}
        <div className="space-y-6">
          <Section title="Acciones manuales">
            <div className="space-y-2">
              <Toggle label="Urgente" checked={!!order.urgent_manual} onChange={(v) => toggle("urgent_manual", v)} />
              <Toggle label="Formulario revisado" checked={!!order.form_reviewed} onChange={(v) => toggle("form_reviewed", v)} />
              <Toggle label="Cliente contactado" checked={!!order.customer_contacted} onChange={(v) => toggle("customer_contacted", v)} />
              <Toggle label="Documentos pendientes" checked={!!order.docs_pending} onChange={(v) => toggle("docs_pending", v)} />
              <Toggle label="Archivado" checked={!!order.archived} onChange={(v) => toggle("archived", v)} />
            </div>
          </Section>

          <Section title="Notas internas">
            <p className="mb-2 text-[11px] text-muted-foreground">Solo visibles para administradores.</p>
            <Textarea
              rows={3}
              placeholder="Escribe una nota interna..."
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              className="bg-surface-2 border-hairline text-sm"
            />
            <Button size="sm" onClick={addNote} className="mt-2 w-full bg-primary text-primary-foreground hover:bg-primary/90">Guardar nota</Button>
            <ul className="mt-4 space-y-2">
              {notes.map((n) => (
                <li key={n.id} className="rounded border border-hairline bg-surface-2 p-2 text-sm">
                  <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                    <span>{new Date(n.created_at).toLocaleString()}</span>
                    <button onClick={() => deleteNote(n.id)} className="hover:text-destructive">eliminar</button>
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-foreground">{n.note}</p>
                </li>
              ))}
              {notes.length === 0 && <li className="text-xs text-muted-foreground">Sin notas todavía.</li>}
            </ul>
          </Section>

          <Section title="Timeline">
            <ol className="space-y-3">
              {events.map((e) => (
                <li key={e.id} className="border-l-2 border-hairline pl-3">
                  <div className="text-xs font-medium text-foreground">{EVENT_LABELS[e.event_type] ?? e.event_type}</div>
                  <div className="text-[10px] text-muted-foreground">{new Date(e.created_at).toLocaleString()} · {e.actor}</div>
                  {e.payload && (
                    <div className="mt-0.5 text-[11px] text-muted-foreground">
                      {e.event_type === "status_changed" ? `${e.payload.from} → ${e.payload.to}` :
                       e.event_type === "note_added" ? `"${e.payload.preview}"` :
                       e.event_type === "payment_confirmed" ? `${money(e.payload.total ?? 0)}` :
                       null}
                    </div>
                  )}
                </li>
              ))}
              {events.length === 0 && <li className="text-xs text-muted-foreground">Sin eventos.</li>}
            </ol>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children, anchorId }: { title: string; children: React.ReactNode; anchorId?: string }) {
  return (
    <div id={anchorId} className="rounded-lg border border-hairline bg-surface p-5 scroll-mt-24">
      <h3 className="font-display text-sm uppercase tracking-wider text-label-accent">{title}</h3>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <dl className="grid gap-3 md:grid-cols-2">{children}</dl>;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm text-foreground">{children}</dd>
    </div>
  );
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex cursor-pointer items-center gap-2 rounded border border-hairline bg-surface-2 p-2 text-sm">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} />
      <span className="text-foreground">{label}</span>
    </label>
  );
}

function LegalCheck({ label, ok, version }: { label: string; ok: boolean; version?: string | null }) {
  return (
    <div className="flex items-center gap-2 rounded border border-hairline bg-surface px-2 py-1">
      <span className={ok ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}>{ok ? "✓" : "○"}</span>
      <span className="flex-1 text-foreground">{label}</span>
      {version && <span className="font-mono text-[10px] text-muted-foreground">{version}</span>}
    </div>
  );
}
